import { useEffect } from "react";
import { CapacitorHttp, Capacitor, registerPlugin } from "@capacitor/core";
import { validateNativeLocation } from "../lib/nativeLocation.mjs";

/**
 * `@capacitor-community/background-geolocation` is a pure-native plugin —
 * it has no JS bridge file, only iOS/Android sources. We surface it via
 * Capacitor's `registerPlugin` so the same import compiles cleanly on
 * web (returns a proxy that no-ops) and works natively.
 */
const BackgroundGeolocation = registerPlugin("BackgroundGeolocation");

const emitStatus = (status, detail = {}) => {
  window.dispatchEvent(new CustomEvent("proximity:background-location-status", {
    detail: { status, at: Date.now(), ...detail },
  }));
};

/**
 * Native background telemetry.
 *
 * On iOS/Android the browser's `navigator.geolocation.watchPosition` gets
 * suspended when the app is backgrounded. This hook wires up the
 * @capacitor-community/background-geolocation plugin so the OS's own
 * significant-change / foreground-service GPS pipeline keeps posting
 * coordinates to our REAL backend (`/api/locations/update`) while the
 * user's phone is in their pocket.
 *
 * • Web / browser  → no-op (the existing LocationTracker component
 *   handles this via `navigator.geolocation`).
 * • iOS            → uses NSLocationAlwaysAndWhenInUseUsage + the
 *   `location` background mode declared in Info.plist.
 * • Android        → uses a foreground service with the
 *   ACCESS_BACKGROUND_LOCATION permission declared in the manifest.
 *
 * The hook exits cleanly on unmount and can be re-entered when the auth
 * user changes.
 */
export default function useNativeTelemetry(sessionToken, backendUrl, enabled = false, backgroundEnabled = false) {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    // Foreground tracking starts only after the dashboard's precise-location
    // consent. Supplying backgroundEnabled adds the persistent Android
    // notification and requests the separate screen-off behavior.
    if (!enabled || !sessionToken || !backendUrl) {
      emitStatus(enabled ? "waiting_for_signin" : "disabled");
      return;
    }

    let watcherId = null;
    let cancelled = false;

    const start = async () => {
      emitStatus("starting");
      try {
        // The @capacitor-community plugin uses `addWatcher` instead of
        // .start(); it fires the callback with each new fix.
        const options = {
            requestPermissions: true,
            stale: false,
            // 25 metres between saved fixes keeps battery drain modest while
            // still catching the ~5 mph walking case within a few seconds.
            distanceFilter: 25,
        };
        if (backgroundEnabled) {
          options.backgroundTitle = "Proximity is sharing your location";
          options.backgroundMessage = "Tap to open. Your accepted contacts can see your location.";
        }
        watcherId = await BackgroundGeolocation.addWatcher(
          options,
          async (location, error) => {
            if (cancelled) return;
            if (error) {
              const permissionDenied = error?.code === "NOT_AUTHORIZED";
              emitStatus(permissionDenied ? "permission_denied" : "location_error", {
                code: error?.code || null,
              });
              return;
            }
            if (!location) return;
            const fix = validateNativeLocation(location);
            if (!fix.ok && fix.reason === "invalid_coordinates") {
              emitStatus("invalid_fix");
              return;
            }
            // Never let a cell-tower/Wi-Fi estimate move a contact marker or
            // drive a geofence transition. The next accurate fix will retry.
            if (!fix.ok) {
              emitStatus("waiting_for_precise_fix", { accuracy: fix.accuracy ?? null });
              return;
            }
            const { lat, lng, accuracy } = fix;
            try {
              const response = await CapacitorHttp.post({
                url: `${backendUrl}/api/locations/update`,
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${sessionToken}`,
                },
                data: {
                  lat,
                  lng,
                  accuracy,
                  speed: location.speed,
                  heading: location.bearing,
                  battery: location.battery?.level,
                  source: backgroundEnabled ? "native_bg" : "native_fg",
                },
              });
              if (response?.status < 200 || response?.status >= 300) {
                emitStatus(response?.status === 401 ? "signin_required" : "upload_failed", { httpStatus: response?.status });
                return;
              }
              emitStatus("running", { accuracy, lastUploadAt: Date.now(), backgroundEnabled });
            } catch (e) {
              emitStatus("upload_failed");
              // eslint-disable-next-line no-console
              console.warn("bg-geolocation post failed:", e?.message || e);
            }
          }
        );
        if (!cancelled) emitStatus("waiting_for_fix");
      } catch (e) {
        emitStatus(e?.code === "NOT_AUTHORIZED" ? "permission_denied" : "start_failed", {
          code: e?.code || null,
        });
        // eslint-disable-next-line no-console
        console.warn("bg-geolocation start failed:", e?.message || e);
      }
    };

    start();

    return () => {
      cancelled = true;
      if (watcherId) {
        Promise.resolve(BackgroundGeolocation.removeWatcher({ id: watcherId })).catch(() => {});
      }
      emitStatus("stopped");
    };
  }, [sessionToken, backendUrl, enabled, backgroundEnabled]);
}
